/**
 * Personal del policlínico — vista única de todas las personas que trabajan ahí.
 *
 * Requiere usuario:read; usuario:create / usuario:update para las cuentas y
 * profesional:create para dar de alta personal de salud.
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, Stethoscope, UserPlus } from "lucide-react";
import { type Profesional, listProfesionales } from "../api/profesionales";
import { CreateProfesionalDialog } from "./ProfesionalesListPage";
import {
  type Rol,
  type Usuario,
  type UsuarioCreate,
  type UsuarioUpdate,
  createUsuario,
  listRoles,
  listUsuarios,
  updateUsuario,
} from "../api/users";
import { ApiError, formatApiError } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { useBase } from "../auth/TenantBrandingProvider";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Dialog,
  Field,
  Input,
} from "../components/ui";

type Tipo = "todos" | "clinico" | "administrativo" | "sin_cuenta";

/** Una persona del policlínico, venga de la ficha profesional, de la cuenta, o de ambas. */
interface Fila {
  key: string;
  nombre: string;
  /** Ficha de profesional de salud, si la tiene. */
  prof: Profesional | null;
  /** Cuenta de acceso, si la tiene. */
  cuenta: Usuario | null;
  activo: boolean;
}

const FILTROS: { id: Tipo; label: string }[] = [
  { id: "todos", label: "Todos" },
  { id: "clinico", label: "Atienden pacientes" },
  { id: "administrativo", label: "Administrativos" },
  { id: "sin_cuenta", label: "Sin acceso al sistema" },
];

/**
 * Personal del policlínico: TODAS las personas que trabajan ahí, en una lista.
 *
 * Antes esto estaba partido en dos secciones —"Profesionales" y "Personal"—
 * que era el modelo de datos (tabla `profesional` vs tabla `usuario`) filtrado
 * a la interfaz. Para el negocio no son dos cosas: es una persona que puede
 * atender pacientes, entrar al sistema, o ambas. Un médico salía en las dos
 * listas y no quedaba claro dónde darlo de alta.
 *
 * Las dos capacidades son independientes y la lista las muestra por separado:
 *   · Ficha profesional → colegiatura, especialidades, sedes, agenda
 *   · Cuenta de acceso  → email, contraseña, roles
 * Un obstetra puede no tener cuenta; recepción no tiene ficha profesional.
 */
export function PersonalListPage() {
  const { me } = useAuth();
  const base = useBase();
  const puedeCrear = me?.permisos.includes("usuario:create") ?? false;
  const puedeEditar = me?.permisos.includes("usuario:update") ?? false;
  const puedeCrearProf = me?.permisos.includes("profesional:create") ?? false;

  const [createOpen, setCreateOpen] = useState(false);
  const [createProfOpen, setCreateProfOpen] = useState(false);
  const [editing, setEditing] = useState<Usuario | null>(null);
  const [filtro, setFiltro] = useState<Tipo>("todos");
  const [busqueda, setBusqueda] = useState("");

  const { data: usuarios = [], isLoading, error } = useQuery({
    // Sin `soloPersonal` aquí salían las cuentas del portal del paciente:
    // esta pantalla es de quien TRABAJA en el policlínico.
    queryKey: ["usuarios", "personal"],
    queryFn: () => listUsuarios({ soloPersonal: true }),
  });
  const { data: profesionales = [] } = useQuery({
    queryKey: ["profesionales", "all"],
    queryFn: () => listProfesionales(),
  });
  const { data: roles = [] } = useQuery({ queryKey: ["roles"], queryFn: listRoles });

  const roleName = useMemo(() => {
    const m = new Map<number, string>();
    roles.forEach((r) => m.set(r.id, r.nombre));
    return m;
  }, [roles]);

  // La unión se hace por persona_id: profesional y cuenta cuelgan de la misma
  // Persona. Sin eso, un médico aparecería dos veces en su propia lista.
  const filas = useMemo<Fila[]>(() => {
    const porPersona = new Map<number, Usuario>();
    for (const u of usuarios) {
      if (u.persona_id != null) porPersona.set(u.persona_id, u);
    }
    const filasProf: Fila[] = profesionales.map((p) => ({
      key: `prof-${p.id}`,
      nombre: p.nombre_completo,
      prof: p,
      cuenta: porPersona.get(p.persona_id) ?? null,
      activo: p.activo,
    }));
    const personasClinicas = new Set(profesionales.map((p) => p.persona_id));
    const filasAdmin: Fila[] = usuarios
      .filter((u) => u.persona_id == null || !personasClinicas.has(u.persona_id))
      .map((u) => ({
        key: `user-${u.id}`,
        // La PERSONA primero. Aquí salía el `username`, que en las cuentas del
        // mostrador es el puesto —"Recepción", "Caja"—, así que la lista de
        // personal no decía quién trabaja aquí: buscabas a Karina y no estaba,
        // aunque llevara meses dando citas. El username solo se usa cuando la
        // cuenta no tiene persona detrás, que hoy son los dos superadmin.
        nombre: u.persona_nombre || u.username || u.email.split("@")[0],
        prof: null,
        cuenta: u,
        activo: u.activo,
      }));
    return [...filasProf, ...filasAdmin].sort((a, b) =>
      a.nombre.localeCompare(b.nombre),
    );
  }, [usuarios, profesionales]);

  const visibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return filas.filter((f) => {
      if (filtro === "clinico" && !f.prof) return false;
      if (filtro === "administrativo" && f.prof) return false;
      if (filtro === "sin_cuenta" && f.cuenta) return false;
      if (!q) return true;
      return (
        f.nombre.toLowerCase().includes(q) ||
        (f.cuenta?.email ?? "").toLowerCase().includes(q) ||
        (f.prof?.colegiatura ?? "").toLowerCase().includes(q) ||
        (f.prof?.num_documento ?? "").includes(q) ||
        (f.cuenta?.persona_documento ?? "").includes(q) ||
        // Por el puesto también: quien busca "recepción" quiere esa cuenta, y
        // ahora que la fila se llama "Bravo Ynga, Karina" ya no la encontraría.
        (f.cuenta?.username ?? "").toLowerCase().includes(q)
      );
    });
  }, [filas, filtro, busqueda]);

  const conteo = (t: Tipo) =>
    t === "todos"
      ? filas.length
      : t === "clinico"
        ? filas.filter((f) => f.prof).length
        : t === "administrativo"
          ? filas.filter((f) => !f.prof).length
          : filas.filter((f) => !f.cuenta).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Personal</h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-500">
            Todas las personas del policlínico. Quien atiende pacientes tiene ficha
            profesional (colegiatura, especialidades y agenda); quien usa el sistema
            tiene cuenta de acceso. Una persona puede tener las dos, o solo una.
          </p>
        </div>
        <div className="flex gap-2">
          {/* Dos altas distintas porque son dos cosas distintas: quien atiende
              necesita colegiatura y especialidades; quien solo usa el sistema,
              un email y unos roles. Un botón único con un paso previo de
              "¿qué tipo?" sería un clic extra para la misma decisión. */}
          {puedeCrearProf && (
            <Button onClick={() => setCreateProfOpen(true)}>
              <Stethoscope className="h-4 w-4" />
              Personal de salud
            </Button>
          )}
          {puedeCrear && (
            <Button variant="ghost" onClick={() => setCreateOpen(true)}>
              <UserPlus className="h-4 w-4" />
              Administrativo
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap gap-1.5">
              {FILTROS.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFiltro(f.id)}
                  aria-pressed={filtro === f.id}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition focus:outline-none focus:ring-2 focus:ring-[color:var(--color-link)] ${
                    filtro === f.id
                      ? "bg-[color:var(--color-link)] text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {f.label} ({conteo(f.id)})
                </button>
              ))}
            </div>
            <div className="relative ml-auto">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
                aria-hidden="true"
              />
              <Input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Buscar por nombre, email, DNI o colegiatura…"
                aria-label="Buscar personal"
                className="w-72 pl-8"
              />
            </div>
          </div>
        </CardHeader>

        <CardBody className="p-0">
          {isLoading && (
            <div className="px-6 py-12 text-center text-sm text-slate-400">Cargando…</div>
          )}
          {error && (
            <div className="px-6 py-4">
              <Alert>No se pudo cargar el personal.</Alert>
            </div>
          )}
          {!isLoading && visibles.length === 0 && (
            <div className="px-6 py-12 text-center text-sm text-slate-500">
              {busqueda ? "Nadie coincide con la búsqueda." : "Aún no hay personal registrado."}
            </div>
          )}

          {visibles.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-y border-slate-200 bg-slate-50">
                  <tr>
                    <Th>Nombre</Th>
                    <Th>Función</Th>
                    <Th>Ficha profesional</Th>
                    <Th>Acceso al sistema</Th>
                    <Th>Estado</Th>
                    <Th>&nbsp;</Th>
                  </tr>
                </thead>
                <tbody>
                  {visibles.map((f) => (
                    <tr key={f.key} className="border-b border-slate-100">
                      <td className="px-6 py-3">
                        {f.prof ? (
                          <Link
                            to={`${base}/profesionales/${f.prof.id}`}
                            className="font-medium text-[color:var(--color-link)] hover:text-[color:var(--color-link)] hover:underline"
                          >
                            {f.nombre}
                          </Link>
                        ) : (
                          <span className="font-medium text-slate-800">{f.nombre}</span>
                        )}
                        {f.prof ? (
                          <div className="font-mono text-xs text-slate-400">
                            DNI {f.prof.num_documento}
                          </div>
                        ) : (
                          f.cuenta?.persona_documento && (
                            <div className="font-mono text-xs text-slate-400">
                              DNI {f.cuenta.persona_documento}
                            </div>
                          )
                        )}
                      </td>

                      <td className="px-6 py-3">
                        {f.prof ? (
                          <div className="flex flex-wrap gap-1">
                            {f.prof.especialidades.length === 0 ? (
                              <Badge variant="warning">Sin especialidad</Badge>
                            ) : (
                              f.prof.especialidades.map((e) => (
                                <Badge key={e.id}>{e.nombre}</Badge>
                              ))
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {(f.cuenta?.rol_ids ?? []).length === 0 ? (
                              <span className="text-xs text-slate-400">Sin rol</span>
                            ) : (
                              f.cuenta!.rol_ids.map((rid) => (
                                <Badge key={rid}>{roleName.get(rid) ?? `#${rid}`}</Badge>
                              ))
                            )}
                          </div>
                        )}
                      </td>

                      <td className="px-6 py-3">
                        {f.prof ? (
                          <span className="font-mono text-xs text-slate-600">
                            {f.prof.tipo_colegio} {f.prof.colegiatura}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">
                            No atiende pacientes
                          </span>
                        )}
                      </td>

                      <td className="px-6 py-3">
                        {f.cuenta ? (
                          <span className="text-xs text-slate-600">{f.cuenta.email}</span>
                        ) : (
                          <Badge variant="warning">Sin cuenta</Badge>
                        )}
                      </td>

                      <td className="px-6 py-3">
                        <Badge variant={f.activo ? "success" : "danger"}>
                          {f.activo ? "Activo" : "Inactivo"}
                        </Badge>
                      </td>

                      <td className="px-6 py-3 text-right">
                        {f.cuenta && puedeEditar && (
                          <button
                            onClick={() => setEditing(f.cuenta)}
                            className="text-xs font-medium text-slate-500 hover:text-[color:var(--color-link)]"
                          >
                            Editar acceso
                          </button>
                        )}
                        {!f.cuenta && f.prof && (
                          <Link
                            to={`${base}/profesionales/${f.prof.id}`}
                            className="text-xs font-medium text-slate-500 hover:text-[color:var(--color-link)]"
                          >
                            Dar acceso
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <CreateProfesionalDialog
        open={createProfOpen}
        onClose={() => setCreateProfOpen(false)}
      />
      <CreatePersonalDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        roles={roles}
      />
      <EditPersonalDialog usuario={editing} onClose={() => setEditing(null)} roles={roles} />
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left px-6 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">
      {children}
    </th>
  );
}

/**
 * Todos los roles, sin excepciones y sin deducciones.
 *
 * El rol `medico` estuvo escondido aquí un tiempo, con el argumento de que "se
 * gestiona desde la ficha profesional". Dos errores en uno:
 *
 * 1. Quien abría la ficha de un médico no encontraba "Médico" por ninguna
 *    parte: ni para ponerlo, ni para quitárselo a quien dejó de pasar consulta.
 * 2. Tener ficha profesional NO es ser médico. Una asistenta o un practicante
 *    tienen ficha y no les toca ese rol.
 *
 * La ficha dice qué sabe hacer alguien; el rol, qué puede hacer en el sistema.
 * Son cosas distintas y las decide quien administra el personal, no esta
 * pantalla.
 */
function RolesPicker({
  roles,
  selected,
  onToggle,
}: {
  roles: Rol[];
  selected: number[];
  onToggle: (id: number) => void;
}) {
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 gap-1 border border-slate-200 rounded-lg p-2 max-h-44 overflow-y-auto">
      {roles.map((r) => {
        const checked = selected.includes(r.id);
        return (
          <label
            key={r.id}
            className={`flex items-center gap-2 text-sm cursor-pointer rounded p-1.5 hover:bg-slate-50 ${
              checked ? "bg-[color-mix(in_srgb,var(--color-link)_8%,white)]" : ""
            }`}
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={() => onToggle(r.id)}
              className="rounded border-slate-300 text-[color:var(--color-link)] focus:ring-[color:var(--color-link)]"
            />
            <span className="text-slate-700">{r.nombre}</span>
          </label>
          );
        })}
      </div>
    </div>
  );
}

function randomPassword(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  let out = "";
  const arr = new Uint32Array(12);
  crypto.getRandomValues(arr);
  for (let i = 0; i < 12; i++) out += chars[arr[i] % chars.length];
  return out;
}

function CreatePersonalDialog({
  open,
  onClose,
  roles,
}: {
  open: boolean;
  onClose: () => void;
  roles: Rol[];
}) {
  const queryClient = useQueryClient();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rolIds, setRolIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setUsername("");
    setEmail("");
    setPassword("");
    setRolIds([]);
    setError(null);
  }

  const mutation = useMutation({
    mutationFn: (payload: UsuarioCreate) => createUsuario(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["usuarios"] });
      onClose();
      reset();
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        setError("Ya existe una cuenta con ese email.");
      } else if (err instanceof ApiError && err.status === 422) {
        setError("Datos inválidos. La contraseña debe tener al menos 8 caracteres.");
      } else {
        setError(formatApiError(err, "No se pudo crear el personal."));
      }
    },
  });

  function toggleRol(id: number) {
    setRolIds((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate({
      username: username.trim() || undefined,
      email: email.trim(),
      password,
      rol_ids: rolIds,
    });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Nuevo personal"
      description="Personas que usan el sistema pero no atienden pacientes: recepción, caja, contabilidad, administración."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <Alert>{error}</Alert>}
        <Field label="Nombre y apellido" htmlFor="p_nombre">
          <Input
            id="p_nombre"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="María Fernández"
            required
          />
        </Field>
        <Field label="Email" htmlFor="p_email" hint="Con este email inicia sesión.">
          <Input
            id="p_email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="recepcion@policlinico.com"
            required
          />
        </Field>
        <Field label="Contraseña" htmlFor="p_pass" hint="Mínimo 8 caracteres. Compártela con la persona.">
          <div className="flex gap-2">
            <Input
              id="p_pass"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              required
              className="font-mono"
            />
            <Button type="button" variant="ghost" onClick={() => setPassword(randomPassword())}>
              Generar
            </Button>
          </div>
        </Field>
        <div>
          <p className="text-sm font-medium text-slate-700 mb-1.5">Roles</p>
          {roles.length === 0 ? (
            <p className="text-xs text-slate-500">Cargando roles…</p>
          ) : (
            <RolesPicker roles={roles} selected={rolIds} onToggle={toggleRol} />
          )}
        </div>
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            Crear personal
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function EditPersonalDialog({
  usuario,
  onClose,
  roles,
}: {
  usuario: Usuario | null;
  onClose: () => void;
  roles: Rol[];
}) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [activo, setActivo] = useState(true);
  const [rolIds, setRolIds] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (usuario) {
      setEmail(usuario.email);
      setActivo(usuario.activo);
      setRolIds(usuario.rol_ids);
      setError(null);
    }
  }, [usuario]);

  const mutation = useMutation({
    mutationFn: (payload: UsuarioUpdate) => updateUsuario(usuario!.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["usuarios"] });
      onClose();
    },
    onError: (err) => setError(formatApiError(err, "No se pudo guardar.")),
  });

  function toggleRol(id: number) {
    setRolIds((prev) => (prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id]));
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate({ email: email.trim(), activo, rol_ids: rolIds });
  }

  return (
    <Dialog
      open={usuario !== null}
      onClose={onClose}
      title="Editar personal"
      // La persona, y el puesto detrás entre paréntesis. Editando roles hay
      // que tener delante a QUIÉN se los estás dando.
      description={
        usuario?.persona_nombre
          ? usuario.username
            ? `${usuario.persona_nombre} · ${usuario.username}`
            : usuario.persona_nombre
          : (usuario?.username ?? usuario?.email)
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <Alert>{error}</Alert>}
        <Field label="Email" htmlFor="e_email">
          <Input
            id="e_email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </Field>
        <div>
          <p className="text-sm font-medium text-slate-700 mb-1.5">Roles</p>
          {roles.length === 0 ? (
            <p className="text-xs text-slate-500">Cargando roles…</p>
          ) : (
            <RolesPicker roles={roles} selected={rolIds} onToggle={toggleRol} />
          )}
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={activo}
            onChange={(e) => setActivo(e.target.checked)}
            className="rounded border-slate-300 text-[color:var(--color-link)] focus:ring-[color:var(--color-link)]"
          />
          Cuenta activa
        </label>
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={mutation.isPending}>
            Guardar cambios
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
